/**
 * Accessibility styles hook
 *
 * Provides computed CSS values for accessibility settings from preferences.
 * Use these values in component styles to ensure proper text rendering
 * and respect user accessibility preferences.
 */

import { usePreferencesStore } from '@/stores';

// Line height values for different spacing preferences
// Higher values prevent text cutoff (especially for descenders like g, y, p, q, j)
export const LINE_HEIGHT_MAP = {
  tight: 1.3,
  regular: 1.5,
  relaxed: 1.75,
  loose: 2.0,
} as const;

// Letter spacing values for different preferences
export const LETTER_SPACING_MAP = {
  normal: 'normal',
  wide: '0.05em',
} as const;

export type LineSpacing = keyof typeof LINE_HEIGHT_MAP;
export type LetterSpacing = keyof typeof LETTER_SPACING_MAP;

export interface AccessibilityStyles {
  lineHeight: number;
  letterSpacing: string;
  lineSpacing: LineSpacing;
  letterSpacingPreference: LetterSpacing;
}

/**
 * Hook to get computed accessibility styles from user preferences
 *
 * @example
 * ```tsx
 * const { lineHeight, letterSpacing } = useAccessibilityStyles();
 *
 * return (
 *   <Box sx={{ lineHeight, letterSpacing }}>
 *     Text content
 *   </Box>
 * );
 * ```
 */
export function useAccessibilityStyles(): AccessibilityStyles {
  const lineSpacing = usePreferencesStore((s) => s.lineSpacing);
  const letterSpacingPreference = usePreferencesStore((s) => s.letterSpacing);

  return {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPreference],
    lineSpacing,
    letterSpacingPreference,
  };
}

/**
 * Get line height value for a given spacing preference
 * Use this when you need the value outside of a React component
 */
export function getLineHeight(spacing: LineSpacing): number {
  return LINE_HEIGHT_MAP[spacing];
}

/**
 * Get letter spacing value for a given preference
 * Use this when you need the value outside of a React component
 */
export function getLetterSpacing(spacing: LetterSpacing): string {
  return LETTER_SPACING_MAP[spacing];
}

export default useAccessibilityStyles;
